import {Paper, withWidth, MenuItem, Grid, Select} from '@material-ui/core';
import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import './EnrollmentFileVolumeAndTurnAroundChart.sass';
import {
  FlexibleXYPlot,
  XAxis,
  YAxis,
  VerticalGridLines,
  HorizontalGridLines,
  VerticalBarSeries,
  HorizontalBarSeries,
  LineMarkSeries,
  LineMarkSeriesPoint,
  LabelSeries,
} from 'react-vis';
import {WithWidthProps} from '@material-ui/core/withWidth';
import {LifeCycleReportChartDetailsPayload} from 'Enrollment/models/LifeCycleReportChartDetailsPayload';
import {getLifeCycleReportChartDetails} from 'Enrollment/ducks/enrollmentFileProcessing';

type EnrollmentFileVolumeAndTurnAroundChartProps = {
  lifeCycleReportChartDetails: LifeCycleReportChartDetailsPayload;
} & WithWidthProps;

const EnrollmentFileVolumeAndTurnAroundChart = (props: EnrollmentFileVolumeAndTurnAroundChartProps) => {
  const dispatch = useDispatch();
  const WeeklyChart = 'Weekly';
  const MonthlyChart = 'Monthly';
  const chartTypeDropDown = [WeeklyChart, MonthlyChart];
  const isMobile = ['sm', 'xs', 'md'].includes(props.width || 'xl');
  const BarSeries: any = isMobile ? HorizontalBarSeries : VerticalBarSeries;
  const [selectedChartId, setSelectedChartId] = useState(WeeklyChart);
  const [fileVolumeCustomTitle, setFileVolumeCustomTitle] = useState('');
  const [, setHintAxes] = useState<LineMarkSeriesPoint | null>(null);
  const [, setHintText] = useState<string | null>(null);
  const {lifeCycleReportChartDetails} = props;
  const fileVolumeData =
    lifeCycleReportChartDetails.fileVolumesChart && lifeCycleReportChartDetails.fileVolumesChart.length > 0
      ? lifeCycleReportChartDetails.fileVolumesChart
      : null;
  const daysToProcessData =
    lifeCycleReportChartDetails.daysToProcessTurnAroundChart &&
    lifeCycleReportChartDetails.daysToProcessTurnAroundChart.length > 0
      ? lifeCycleReportChartDetails.daysToProcessTurnAroundChart
      : null;

  const convertThousandInK = (labelValue: any) => {
    if (labelValue >= 1000) {
      return (labelValue / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return labelValue;
  };

  const fileVolumeChartData = fileVolumeData?.map(record => ({
    x: isMobile ? record.count : record.displayLabel,
    y: isMobile ? record.displayLabel : record.count,
  }));

  const fileVolumeChartLabelData = fileVolumeData?.map(record => ({
    x: isMobile ? record.count : record.displayLabel,
    y: isMobile ? record.displayLabel : record.count ? record.count + 10 : record.count,
    label: convertThousandInK(record.count).toString(),
  }));

  const daysToProcessChartData = daysToProcessData?.map(record => ({
    x: isMobile ? record.count : record.displayLabel,
    y: isMobile ? record.displayLabel : record.count,
  }));

  const daysToProcessChartLabelData = daysToProcessData?.map(record => ({
    x: isMobile ? record.count : record.displayLabel,
    y: isMobile ? record.displayLabel : record.count,
    label: record.count!.toString(),
  }));

  const fileVolumeMaxCountValue = getMaxCount(fileVolumeData);
  const fileVolumeMinCountValue = getMinCount(fileVolumeData);
  const daysToProcessChartMaxCountValue = getMaxCount(daysToProcessData);
  const daysToProcessChartMinCountValue = getMinCount(daysToProcessData);

  useEffect(() => {
    fileVolumeMaxCountValue >= 1000 ? setFileVolumeCustomTitle('(per thousand)') : setFileVolumeCustomTitle('');
  }, [fileVolumeMaxCountValue]);

  const fileVolumeChartProps: any = {
    yDomain: [fileVolumeMinCountValue, fileVolumeMaxCountValue],
  };
  const daysToProcessChartProps: any = {
    yDomain: [daysToProcessChartMinCountValue, daysToProcessChartMaxCountValue],
  };
  const fileVolumeChartMobileProps: any = {
    xDomain: [fileVolumeMinCountValue, fileVolumeMaxCountValue],
  };
  const daysToProcessChartMobileProps: any = {
    xDomain: [daysToProcessChartMinCountValue, daysToProcessChartMaxCountValue],
  };

  const yAxisValueFileVolume = isMobile ? fileVolumeChartMobileProps : fileVolumeChartProps;
  const yAxisValueDaysToProcess = isMobile ? daysToProcessChartMobileProps : daysToProcessChartProps;

  function getMinCount(data: any) {
    return data.reduce((min: any, p: any) => (p.count < min ? p.count : min), data[0].count);
  }
  function getMaxCount(data: any) {
    return data.reduce((max: any, p: any) => (p.count > max ? p.count : max), data[0].count);
  }

  const numberLeadingWithDecimalPlaces = (labelValue: any) => {
    return labelValue.toFixed(2);
  };

  const handleSeriesMouseOver = (e: LineMarkSeriesPoint) => {
    setHintText(isMobile ? e.x.toString() : e.y.toString());
    setHintAxes(e);
  };

  const handleSeriesMouseOut = () => {
    setHintText(null);
  };

  const handleDemoChartChange = (value: string) => {
    setSelectedChartId(value);
    dispatch(getLifeCycleReportChartDetails(value));
  };

  return (
    <Paper className="enrollments-file-volume-chart">
      <div className="enrollments-file-volume-chart__header">
        <Grid container spacing={2} justify="space-between">
          <Grid item>
            <h3 className="enrollments-file-volume-chart__header-title">File Volume &amp; Turnaround Time</h3>
          </Grid>
          <Grid item>
            <Select
              className="enrollment-file-rejects-modal__remove-content-Options"
              label="Monthly"
              onChange={e => handleDemoChartChange(e.target.value as string)}
              value={selectedChartId}
            >
              {chartTypeDropDown.map((type, index) => (
                <MenuItem key={index} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </div>
      <div className="enrollments-file-volume-chart__legend">
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={4} xl={4}>
            <div className="enrollments-file-volume-chart__legend-item">
              <span className="legend-item__indicator daysToProcess" />
              <span className="legend-item__title">Days to Process</span>
            </div>
          </Grid>
          <Grid item xs={12} md={4} lg={4} xl={4}>
            <div className="enrollments-file-volume-chart__legend-item">
              <span className="legend-item__indicator fileVolume" />
              <span className="legend-item__title">Total Files {fileVolumeCustomTitle} </span>
            </div>
          </Grid>
        </Grid>
      </div>
      <div className="enrollments-file-volume-chart__container">
        <FlexibleXYPlot
          height={320}
          stackBy={isMobile ? 'x' : 'y'}
          yType={isMobile ? 'ordinal' : undefined}
          xType={!isMobile ? 'ordinal' : undefined}
          margin={isMobile ? {bottom: 40, top: 40, left: 80, right: 20} : {bottom: 80, top: 20, left: 50, right: 50}}
        >
          <VerticalGridLines />
          <HorizontalGridLines />
          {isMobile ? (
            <XAxis {...yAxisValueFileVolume} tickFormat={(v: number) => convertThousandInK(v)} />
          ) : (
            <XAxis tickLabelAngle={-90} />
          )}
          {isMobile ? <YAxis /> : <YAxis {...yAxisValueFileVolume} tickFormat={(v: number) => convertThousandInK(v)} />}
          {isMobile ? (
            <XAxis orientation="top" {...yAxisValueDaysToProcess} tickFormat={(v: number) => v} />
          ) : (
            <YAxis
              orientation="right"
              {...yAxisValueDaysToProcess}
              tickFormat={(v: number) => numberLeadingWithDecimalPlaces(v)}
            />
          )}

          <BarSeries data={fileVolumeChartData} stack barWidth={0.5} />
          <LabelSeries
            data={fileVolumeChartLabelData}
            labelAnchorX="start"
            labelAnchorY="text-after-edge"
            className="enrollments-file-volume-chart__file-volume-label-series"
          />
          <LabelSeries
            data={daysToProcessChartLabelData}
            labelAnchorX="end"
            labelAnchorY="text-after-edge"
            className="enrollments-file-volume-chart__days-to-process-label-series"
            {...yAxisValueDaysToProcess}
          />
          <LineMarkSeries
            curve={isMobile ? 'curveMonotoneY' : 'curveMonotoneX'}
            data={daysToProcessChartData}
            lineStyle={{stroke: '#0033A0'}}
            markStyle={{stroke: '#0033A0', fill: '#FFFFFF'}}
            onNearestX={graphData => handleSeriesMouseOver(graphData)}
            onSeriesMouseOut={handleSeriesMouseOut}
            {...yAxisValueDaysToProcess}
          />
        </FlexibleXYPlot>
      </div>
    </Paper>
  );
};

export default withWidth()(EnrollmentFileVolumeAndTurnAroundChart);
